/**
 * The booked page's behavior half: the booking read on the website session,
 * its ticket rows, the preparation note, the Telegram line (re-read while the
 * confirmation is still on its way), and the calendar file. Draws nothing.
 */
import { useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { bookingCalendarLinks, CALENDAR_FILE_FAILED } from '@/utils/calendarLinks';

import { useSiteStudio } from '../../api/site';
import { usePieceEmit, usePieceOptions, type PieceInput, type PieceRuntime } from '../../contract/runtime';
import { getClientTimezone } from '../../format';
import { bookedRows, openCalendarFile, telegramLine, useWebBooking } from '../../hooks/useWebBooking';
import { bookingPhoto } from '../../ui/Photo';
import { contract, type BookedActions, type BookedEvents, type BookedState } from './contract';

export type BookedRuntime = PieceRuntime<typeof contract, BookedState, BookedActions>;

export function useBooked(input: PieceInput<BookedEvents> = {}): BookedRuntime {
  const options = usePieceOptions(contract, input.options);
  const emit = usePieceEmit(input.onEvent);
  const { bookingId = '' } = useParams<{ bookingId: string }>();
  const { detail, message } = useWebBooking(bookingId);
  const studio = useSiteStudio().data;
  const tz = useMemo(() => getClientTimezone(), []);
  const b = detail.data;
  const [problem, setProblem] = useState<string | null>(null);

  const actions = useMemo<BookedActions>(
    () => ({
      addToCalendar: () => {
        if (!b) return;
        setProblem(null);
        void openCalendarFile(b).then(() => emit('addedToCalendar', {}), () => setProblem(CALENDAR_FILE_FAILED));
      },
    }),
    [b, emit],
  );

  return {
    state: {
      status: detail.isError ? 'not-found' : b ? 'ready' : 'loading',
      name: b?.service.name ?? '',
      photoUrl: b?.service.imageUrl ?? null,
      photo: b ? bookingPhoto(b.service) : null,
      withName: b?.provider?.name ? `with ${b.provider.name}` : null,
      rows: b ? bookedRows(b, tz, false) : [],
      preparation: b?.service.preparationNotes
        ? { heading: b.service.preparationNotesLabel || 'How to prepare', text: b.service.preparationNotes }
        : null,
      calendar: b ? bookingCalendarLinks(b) : null,
      telegram: telegramLine(message, studio, false),
      bookingHref: `/account/bookings/${encodeURIComponent(bookingId)}`,
      problem,
    },
    actions,
    options,
  };
}
